/**
 * Heartbeat hook for the lesson player (FR-C-2 / FR-C-3 / M12).
 *
 * Server-side aggregation is the source of truth for "did this candidate
 * actually consume the lesson?" — see
 * ``backend/caitapi/routers/lessons.py`` (heartbeat endpoint). This hook
 * does four things:
 *
 *   1. **Track scroll position** of a target element (the article body)
 *      and convert it to a 0..100 percentage.
 *   2. **Track video playback** when a video element ref is provided —
 *      listens for ``timeupdate`` events on the HTML5 ``<video>`` and
 *      derives ``video_pct = (currentTime / duration) * 100``.
 *   3. **Throttle posts** so we never send more than one heartbeat every
 *      ``minIntervalMs`` (default 10s) regardless of how fast the user
 *      scrolls or how chatty the timeupdate events are.
 *   4. **Send an initial heartbeat** on mount so a lesson_progress row
 *      exists from the moment the player loads.
 *
 * The completion-status endpoint reads from that row, so we want a row
 * to exist even before the candidate has scrolled. Subsequent calls
 * UPSERT with GREATEST(...) on the server, so positions only move
 * forward — a stale "scrolled back to top" or "video paused at 5%
 * after watching 100%" beat can't decrement progress.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { sendHeartbeat, sendHeartbeatBeacon } from "@/lib/lesson-api";

interface UseLessonHeartbeatOptions {
  lessonId: string;
  /** Element whose scroll position the heartbeat tracks. If null,
   *  scroll_pct stays at 0 (e.g. lesson body hasn't mounted yet). */
  scrollTarget: HTMLElement | null;
  /** HTML5 ``<video>`` element whose playback the heartbeat tracks.
   *  If null (lesson has no video), video_pct stays at 0 and the
   *  completion-status server-side logic skips the video threshold
   *  via the ``video_required = (lesson.video_url IS NOT NULL)`` gate. */
  videoTarget?: HTMLVideoElement | null;
  /** Minimum interval between server posts. Default 10s. */
  minIntervalMs?: number;
  /** Test seam: override the network call. Defaults to the real API. */
  postHeartbeat?: typeof sendHeartbeat;
}

interface UseLessonHeartbeatState {
  /** Last server-confirmed scroll_pct (after GREATEST upsert). */
  scrollPct: number;
  /** Last server-confirmed video_pct. */
  videoPct: number;
  /** True once at least one heartbeat has landed. */
  hasInitialHeartbeat: boolean;
}

function computeScrollPct(target: HTMLElement | null): number {
  if (!target) return 0;
  // For an article body: scroll progress is (top + visible / scrollHeight).
  // Window-level scroll: percentage of viewport that has scrolled past
  // the element's top, capped at element height.
  const rect = target.getBoundingClientRect();
  const elementHeight = rect.height;
  if (elementHeight <= 0) return 0;
  // How much of the element is above the viewport top?
  const scrolledPast = Math.max(0, -rect.top);
  // Plus the visible portion of the viewport that's still inside the element.
  const viewportH = window.innerHeight || document.documentElement.clientHeight;
  const visibleEnd = Math.min(elementHeight, scrolledPast + viewportH);
  const pct = Math.round((visibleEnd / elementHeight) * 100);
  return Math.max(0, Math.min(100, pct));
}

function computeVideoPct(target: HTMLVideoElement | null): number {
  if (!target) return 0;
  const duration = target.duration;
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  const pct = Math.round((target.currentTime / duration) * 100);
  return Math.max(0, Math.min(100, pct));
}

export function useLessonHeartbeat({
  lessonId,
  scrollTarget,
  videoTarget = null,
  minIntervalMs = 10_000,
  postHeartbeat = sendHeartbeat,
}: UseLessonHeartbeatOptions): UseLessonHeartbeatState {
  const [state, setState] = useState<UseLessonHeartbeatState>({
    scrollPct: 0,
    videoPct: 0,
    hasInitialHeartbeat: false,
  });

  const lastSentAtRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const lastSentSinceStartRef = useRef<number>(performance.now());
  // Track latest video high-water mark separately so the throttle's
  // scheduled flush sends the most recent number, not whatever was
  // captured when the timer started.
  const latestVideoPctRef = useRef<number>(0);
  // FIX-PLAN-2026-06-02 Bug 2.2: track the latest scroll pct in a ref so
  // the pagehide flush (which fires outside React's render cycle) can
  // read the most-recent value without re-renders. Without this, a quick
  // reload before the throttle window flushes loses unposted progress.
  const latestScrollPctRef = useRef<number>(0);

  // Always send the latest known pcts, capped server-side. We also send
  // the watch_seconds_delta to accumulate time-on-lesson.
  const send = useCallback(
    async (scrollPct: number, videoPct: number) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const now = performance.now();
      const deltaSec = Math.max(
        0,
        Math.min(3600, Math.round((now - lastSentSinceStartRef.current) / 1000)),
      );
      lastSentSinceStartRef.current = now;
      try {
        const response = await postHeartbeat(lessonId, {
          video_pct: videoPct,
          scroll_pct: scrollPct,
          watch_seconds_delta: deltaSec,
        });
        setState({
          scrollPct: response.scroll_pct,
          videoPct: response.video_pct,
          hasInitialHeartbeat: true,
        });
        lastSentAtRef.current = now;
      } catch {
        // Swallow — heartbeats are best-effort. Future retry/backoff
        // belongs here, but never throw to the render path.
      } finally {
        inFlightRef.current = false;
      }
    },
    [lessonId, postHeartbeat],
  );

  // Initial heartbeat on mount so a lesson_progress row exists.
  useEffect(() => {
    void send(computeScrollPct(scrollTarget), computeVideoPct(videoTarget));
    // Intentionally NOT depending on scrollTarget/videoTarget — initial
    // fire happens once on mount with whatever positions are current
    // at that moment. The scroll + timeupdate effects below pick up
    // changes afterward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  // Throttled scroll listener. We always send the most-recent pcts
  // after the throttle window elapses.
  useEffect(() => {
    if (!scrollTarget) return;

    let pendingScrollPct: number | null = null;
    let timeoutId: number | null = null;

    const flush = (): void => {
      if (pendingScrollPct === null) return;
      const sPct = pendingScrollPct;
      pendingScrollPct = null;
      timeoutId = null;
      void send(sPct, latestVideoPctRef.current);
    };

    const onScroll = (): void => {
      const sPct = computeScrollPct(scrollTarget);
      pendingScrollPct = sPct;
      // Track high-water mark so the pagehide beacon (Bug 2.2) can
      // flush even if no throttled post has fired since the last scroll.
      latestScrollPctRef.current = Math.max(latestScrollPctRef.current, sPct);
      const now = performance.now();
      const elapsed = now - lastSentAtRef.current;
      if (elapsed >= minIntervalMs) {
        flush();
      } else if (timeoutId === null) {
        timeoutId = window.setTimeout(flush, minIntervalMs - elapsed);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [scrollTarget, minIntervalMs, send]);

  // Throttled video timeupdate listener. Same throttle window as
  // scroll; the latest pct is recorded in a ref so any flush sends
  // the most up-to-date number. We also flush immediately when the
  // ``ended`` event fires (candidate watched to the end) so the
  // final 100% is captured without waiting on the next scroll.
  useEffect(() => {
    if (!videoTarget) return;

    let pendingVideoPct: number | null = null;
    let timeoutId: number | null = null;

    const flush = (): void => {
      if (pendingVideoPct === null) return;
      const vPct = pendingVideoPct;
      pendingVideoPct = null;
      timeoutId = null;
      void send(state.scrollPct, vPct);
    };

    const onTimeUpdate = (): void => {
      const vPct = computeVideoPct(videoTarget);
      latestVideoPctRef.current = Math.max(latestVideoPctRef.current, vPct);
      pendingVideoPct = latestVideoPctRef.current;
      const now = performance.now();
      const elapsed = now - lastSentAtRef.current;
      if (elapsed >= minIntervalMs) {
        flush();
      } else if (timeoutId === null) {
        timeoutId = window.setTimeout(flush, minIntervalMs - elapsed);
      }
    };

    const onEnded = (): void => {
      latestVideoPctRef.current = 100;
      pendingVideoPct = 100;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      flush();
    };

    videoTarget.addEventListener("timeupdate", onTimeUpdate);
    videoTarget.addEventListener("ended", onEnded);
    return () => {
      videoTarget.removeEventListener("timeupdate", onTimeUpdate);
      videoTarget.removeEventListener("ended", onEnded);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [videoTarget, minIntervalMs, send, state.scrollPct]);

  // FIX-PLAN-2026-06-02 Bug 2.2: pagehide-flush via the Beacon API.
  //
  // The throttled scroll/timeupdate posts can leave unposted progress
  // in pendingScrollPct / latestVideoPctRef when the user navigates
  // away, reloads, or closes the tab before the throttle window
  // elapses. Before this fix, server-side scroll_pct stayed at 0 even
  // when the candidate had scrolled to the bottom — the 2026-06-02
  // walk repeatedly hit this and the heartbeat had to be sent
  // explicitly to recover.
  //
  // We listen for ``pagehide`` (preferred over ``beforeunload``; fires
  // for bfcache navigations + tab close + reload, and is the modern
  // unload-time event) and also ``visibilitychange`` → 'hidden' as a
  // belt-and-suspenders for browsers that pre-process the pagehide.
  // Both call ``navigator.sendBeacon`` with the latest known pcts so
  // the request survives the unload.
  useEffect(() => {
    const flushBeacon = (): void => {
      const scrollPct = latestScrollPctRef.current;
      const videoPct = latestVideoPctRef.current;
      if (scrollPct === 0 && videoPct === 0) {
        // Nothing to flush — no progress beyond the initial mount.
        return;
      }
      const now = performance.now();
      const deltaSec = Math.max(
        0,
        Math.min(3600, Math.round((now - lastSentSinceStartRef.current) / 1000)),
      );
      // Don't bother updating lastSentSinceStartRef — the page is going
      // away. If the user navigates back via bfcache the next normal
      // send() will compute a delta from the now-stale ref, but that's
      // bounded by the 3600s cap above so worst-case is one inflated
      // watch_seconds delta on bfcache restore.
      sendHeartbeatBeacon(lessonId, {
        scroll_pct: scrollPct,
        video_pct: videoPct,
        watch_seconds_delta: deltaSec,
      });
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "hidden") flushBeacon();
    };
    window.addEventListener("pagehide", flushBeacon);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", flushBeacon);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [lessonId]);

  return state;
}
